/**
 * Owns the socket for one live game and keeps the seat across reconnects.
 *
 * The rejoin token goes in localStorage rather than sessionStorage: phones
 * close tabs and kill backgrounded pages, and losing your seat because the
 * screen locked mid-round would be the single most annoying possible bug.
 */

import { useCallback, useEffect, useRef } from 'react';
import type { Card } from '@shared/types/game';
import type { ClientMessage } from '@shared/types/websocket';
import { GameSocket } from '@/live/socket';
import { rememberSeat, seatFor } from '@/live/sessions';
import { useLive } from '@/store/live';
import { useAuth } from '@/store/auth';

export interface LiveActions {
  ready: (ready: boolean) => void;
  start: () => void;
  /** Seat a computer player, or drop the last one. Host only, lobby only. */
  addBot: () => void;
  removeBot: () => void;
  bid: (bid: number) => void;
  play: (card: Card) => void;
  /** Play the only card in hand without naming it — for the blind round. */
  playBlind: () => void;
}

/**
 * @param code Room code to join.
 * @param name Name to join under; ignored when a rejoin token already exists.
 * @param profileId Who tapped their face, if anyone. A hint for the server.
 */
export function useLiveSession(
  code: string | undefined,
  name: string | null,
  profileId: string | null = null
): LiveActions {
  const socketRef = useRef<GameSocket | null>(null);
  const handle = useLive((state) => state.handle);
  const setStatus = useLive((state) => state.setStatus);
  const reset = useLive((state) => state.reset);

  useEffect(() => {
    if (!code) return;
    const upper = code.toUpperCase();

    const socket = new GameSocket(
      (message) => {
        // Persist the seat handle the moment the server issues it.
        if (message.type === 'joined') {
          rememberSeat({
            code: upper,
            mode: 'live',
            rejoinToken: message.rejoinToken,
            name: message.state.players[message.seat]?.name ?? name ?? '',
          });
        }
        handle(message);
      },
      setStatus,
      // Recomputed on every (re)connect, so a token obtained during this
      // session is used by the reconnect that follows it.
      (): ClientMessage | null => {
        const stored = seatFor(upper);
        if (stored) {
          return {
            type: 'join',
            code: upper,
            playerName: stored.name,
            rejoinToken: stored.rejoinToken,
          };
        }
        if (!name) return null;
        return { type: 'join', code: upper, playerName: name, profileId };
      },
      undefined,
      /*
       * The handshake keeps failing and the browser will not say why — a
       * refused upgrade and a restarting server look identical from here. So
       * ask the side that *can* answer. If this device was revoked, /api/auth/me
       * says so and the gate shows the invite screen instead of a phone quietly
       * reconnecting for the rest of the evening.
       */
      () => void useAuth.getState().refresh()
    );

    socketRef.current = socket;
    socket.connect();

    return () => {
      socket.close();
      socketRef.current = null;
      reset();
    };
  }, [code, name, profileId, handle, setStatus, reset]);

  const send = useCallback((message: ClientMessage) => {
    socketRef.current?.send(message);
  }, []);

  return {
    ready: useCallback((value: boolean) => send({ type: 'ready', ready: value }), [send]),
    start: useCallback(() => send({ type: 'start-game' }), [send]),
    addBot: useCallback(() => send({ type: 'add-bot' }), [send]),
    removeBot: useCallback(() => send({ type: 'remove-bot' }), [send]),
    bid: useCallback((value: number) => send({ type: 'place-bid', bid: value }), [send]),
    play: useCallback((card: Card) => send({ type: 'play-card', card }), [send]),
    playBlind: useCallback(() => send({ type: 'play-blind' }), [send]),
  };
}
